import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  TrendingUp, DollarSign, Users, AlertTriangle,
  Activity, FileText, Clock, Shield, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInsightsSection = () => {
  const [kpis, setKpis] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('kpis');

  useEffect(() => {
    const load = async () => {
      try {
        const [k, t, a, s] = await Promise.all([
          axios.get(`${API}/admin/finance/kpis`),
          axios.get(`${API}/admin/finance/mrr-timeline?months=6`),
          axios.get(`${API}/admin/audit/log?limit=50`),
          axios.get(`${API}/admin/audit/summary`),
        ]);
        setKpis(k.data);
        setTimeline(t.data);
        setAuditLog(a.data);
        setAuditSummary(s.data);
      } catch (e) {
        console.error('insights load', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-insights-section">
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <Activity className="w-6 h-6 text-[#D4AF37]" />
          Insights & Accountability
        </h2>
        <p className="text-white/50">Revenue health and admin audit trail</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10 overflow-x-auto">
        <TabButton active={tab === 'kpis'} onClick={() => setTab('kpis')} label="Finance KPIs" color="#D4AF37" />
        <TabButton active={tab === 'audit'} onClick={() => setTab('audit')} label="Audit Log" color="#3B82F6" />
      </div>

      {tab === 'kpis' && kpis && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <KpiCard
              label="MRR"
              value={`$${kpis.mrr.toLocaleString()}`}
              sub={`$${kpis.arr.toLocaleString()} ARR`}
              icon={DollarSign}
              color="#10B981"
              testid="kpi-mrr"
            />
            <KpiCard
              label="Active Subscribers"
              value={kpis.active_subscribers}
              sub={`${kpis.by_tier.bronze} Bronze · ${kpis.by_tier.silver} Silver · ${kpis.by_tier.gold} Gold`}
              icon={Users}
              color="#D4AF37"
              testid="kpi-active"
            />
            <KpiCard
              label="ARPU"
              value={`$${kpis.arpu.toLocaleString()}`}
              sub="Avg revenue per user"
              icon={TrendingUp}
              color="#3B82F6"
              testid="kpi-arpu"
            />
            <KpiCard
              label="Churn (30d)"
              value={`${kpis.churn_rate}%`}
              sub={`${kpis.churn_30d} lost`}
              icon={AlertTriangle}
              color={kpis.churn_rate > 5 ? '#EF4444' : '#F59E0B'}
              testid="kpi-churn"
            />
          </div>

          {/* Tier Mix */}
          <div className="bg-[#121212] border border-white/10 p-5">
            <h3 className="text-white font-bold mb-4 uppercase tracking-wider text-sm">Subscriber Tier Mix</h3>
            <div className="space-y-3">
              <TierBar tier="Bronze" count={kpis.by_tier.bronze} total={kpis.active_subscribers} color="#CD7F32" />
              <TierBar tier="Silver" count={kpis.by_tier.silver} total={kpis.active_subscribers} color="#C0C0C0" />
              <TierBar tier="Gold" count={kpis.by_tier.gold} total={kpis.active_subscribers} color="#D4AF37" />
            </div>
          </div>

          {/* MRR Timeline */}
          <div className="bg-[#121212] border border-white/10 p-4 sm:p-5 overflow-x-auto">
            <h3 className="text-white font-bold mb-4 uppercase tracking-wider text-sm">MRR · Last 6 Months</h3>
            <div className="flex items-end gap-2 sm:gap-3 h-40 min-w-[320px]">
              {timeline.map((t, i) => {
                const maxMrr = Math.max(...timeline.map(x => x.mrr), 1);
                const pct = (t.mrr / maxMrr) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full bg-black/40 relative" style={{ height: '100%' }}>
                      <div
                        className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#D4AF37] to-[#F5D47B] transition-all"
                        style={{ height: `${pct}%`, minHeight: t.mrr > 0 ? '4px' : '0' }}
                      />
                      <div className="absolute -top-6 left-0 right-0 text-center text-white/70 text-[10px] sm:text-xs">
                        ${(t.mrr / 1000).toFixed(1)}k
                      </div>
                    </div>
                    <span className="text-white/50 text-[9px] sm:text-[10px] uppercase tracking-wider whitespace-nowrap">
                      <span className="sm:hidden">{t.month.split(' ')[0]}</span>
                      <span className="hidden sm:inline">{t.month}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {tab === 'audit' && (
        <>
          {auditSummary && auditSummary.total_actions > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <KpiCard label="Actions (30d)" value={auditSummary.total_actions} icon={Activity} color="#3B82F6" />
              <KpiCard
                label="Top Action"
                value={auditSummary.by_action[0]?.action?.replace(/_/g, ' ') || '—'}
                sub={`${auditSummary.by_action[0]?.count || 0}x`}
                icon={FileText}
                color="#D4AF37"
              />
              <KpiCard
                label="Top Actor"
                value={auditSummary.top_actors[0]?.actor?.split('@')[0] || '—'}
                sub={`${auditSummary.top_actors[0]?.count || 0} actions`}
                icon={Shield}
                color="#10B981"
              />
            </div>
          )}

          <div className="bg-[#121212] border border-white/10">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-white font-bold">Recent Admin Actions</h3>
            </div>
            {auditLog.length === 0 ? (
              <div className="p-12 text-center">
                <Clock className="w-10 h-10 text-white/30 mx-auto mb-3" />
                <p className="text-white/50">No admin actions logged yet. They'll appear here automatically.</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
                {auditLog.map((entry, idx) => (
                  <div key={idx} className="p-4 hover:bg-white/5 flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <Activity className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 mb-1">
                        <p className="text-white text-sm font-medium capitalize break-anywhere">
                          {entry.action.replace(/_/g, ' ')}
                        </p>
                        <span className="text-white/40 text-xs font-mono flex-shrink-0">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-white/50 text-xs break-anywhere">
                        <span className="text-[#D4AF37]">{entry.actor_email}</span>
                        {entry.entity_type && <> · {entry.entity_type}</>}
                        {entry.details?.count && <> · {entry.details.count} items</>}
                      </p>
                      {entry.ip_address && entry.ip_address !== 'unknown' && (
                        <p className="text-white/30 text-[10px] font-mono mt-0.5 break-anywhere">IP: {entry.ip_address}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const TabButton = ({ active, onClick, label, color }) => (
  <button
    onClick={onClick}
    className={`px-5 py-3 text-sm font-medium uppercase tracking-wider transition-colors border-b-2 ${
      active ? 'border-current' : 'text-white/50 border-transparent hover:text-white/80'
    }`}
    style={active ? { color, borderColor: color } : {}}
  >
    {label}
  </button>
);

const KpiCard = ({ label, value, sub, icon: Icon, color, testid }) => (
  <div className="bg-[#121212] border border-white/10 p-4 min-w-0" data-testid={testid}>
    <div className="flex items-center justify-between mb-2 gap-2">
      <span className="text-white/50 text-xs uppercase tracking-wider truncate">{label}</span>
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
    </div>
    <p className="text-xl sm:text-2xl font-black text-white mb-1 break-anywhere">{value}</p>
    {sub && <p className="text-white/40 text-xs break-anywhere">{sub}</p>}
  </div>
);

const TierBar = ({ tier, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-sm">
        <span className="text-white">{tier}</span>
        <span className="text-white/60">{count} ({pct.toFixed(0)}%)</span>
      </div>
      <div className="h-2 bg-black/40 overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

export default AdminInsightsSection;
